/**
 * TechnologyDrawerTabs Component
 *
 * Displays the tab navigation for the technology drawer.
 * Pure presentation component - all state management handled by parent.
 *
 * Fixed: Tab alternation bug by moving tab state from URL to local React state.
 * The parent component (TechnologyDrawer) now manages tab state via useTechnologyDrawerTab
 * hook and passes it as props.
 *
 * @see AssetDrawerTabs for reference implementation
 * @see SeedDrawerTabs for dynamic tab pattern
 */

import { TabOption } from '@/components/chariot-ui';

import TabsComponent from '@/components/Tabs';

export interface TechnologyDrawerTabsProps {
  /** Tab configuration array */
  technologyTabOptions: TabOption[];
  /** Current tab value (controlled by parent via useTechnologyDrawerTab hook) */
  currentTab: string;
  /** Callback when tab changes (controlled by parent) */
  onTabChange: (tab: string) => void;
}

/**
 * TechnologyDrawerTabs renders the tab navigation system for technology details:
 * - Assets Impacted, History, Notes tabs
 * - Keyboard navigation with empty dependencies (tabs stable across row navigation)
 * - Receives tab state from parent (no global state dependency)
 * - Matches Asset drawer spacing with -mt-4 wrapper
 *
 * This is a pure presentation component with no internal state management.
 *
 * @example
 * ```tsx
 * const [currentTab, setCurrentTab] = useTechnologyDrawerTab();
 *
 * <TechnologyDrawerTabs
 *   technologyTabOptions={technologyTabOptions}
 *   currentTab={currentTab}
 *   onTabChange={setCurrentTab}
 * />
 * ```
 */
export function TechnologyDrawerTabs({
  technologyTabOptions,
  currentTab,
  onTabChange,
}: TechnologyDrawerTabsProps) {
  const selectedTab =
    technologyTabOptions.find(tab => tab.value === currentTab) ||
    technologyTabOptions[0];

  return (
    <div className="-mt-4">
      <TabsComponent
        id="technologyDetailsTabs"
        keyboardDependencies={[]}
        tabs={technologyTabOptions}
        selected={selectedTab}
        onSelect={tabOption => {
          onTabChange(tabOption.value);
        }}
      />
    </div>
  );
}
